import React from 'react'
import { useQuery } from '@tanstack/react-query'
import type { MFAAttributeName } from '@mobi/api-types'
import { Spinner } from '@mobi/component-library/Legacy/Spinner'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { spacing } from '@mobi/component-library/Foundations'
import { MfaCodePageText } from './VerificationCodePage.styles'
import { VerificationCodeForm } from '../Components'
import { fetchCurrentCognitoUser } from '@core/Data/Account/useCognitoUser'
import { syncMfaContact } from '../Services/mfaService'
import {
  useMfaCompleteVerification,
  startCognitoAttributeVerification,
} from '../Hooks/useMfaVerification'

const attributeName: MFAAttributeName = 'phone_number'

type OnCompleted = (skipped: boolean) => void

export const SignUpVerificationPage = ({
  accountNumber,
  onCompleted,
}: {
  accountNumber: number
  onCompleted: OnCompleted
}) => {
  const { completeVerification: completeCognitoAttributeVerification } =
    useMfaCompleteVerification()
  const [verificationAttempts, setVerificationAttempts] = React.useState<number>(0)

  const { isLoading: isQueryLoading, data: cognitoUser } = useQuery({
    queryFn: async () => {
      await syncMfaContact(accountNumber)

      const user = await fetchCurrentCognitoUser()

      // check there is a phone number
      if (!user.userAttributes[attributeName]) {
        throw new Error('Error: No phone number for this user')
      }

      await startCognitoAttributeVerification({ attributeName })
      return user
    },
    queryKey: ['SignUpVerificationPage', accountNumber],
    retry: false,
    refetchOnWindowFocus: false,
  })

  const handleResendVerification = async () => {
    try {
      await startCognitoAttributeVerification({ attributeName })
    } finally {
      setVerificationAttempts(count => count + 1)
    }
  }

  const handleCompleteVerification = async (code: string) => {
    try {
      await completeCognitoAttributeVerification({ attributeName, code })
    } finally {
      setVerificationAttempts(count => count + 1)
    }

    onCompleted(false)
  }

  const phoneNumber = cognitoUser?.userAttributes.phone_number

  return (
    <>
      <Flex gap={spacing.smx1} flexDirection='column'>
        <VerificationCodeForm
          onVerify={handleCompleteVerification}
          onResend={handleResendVerification}
          title='Verify your contact details'
          analyticsFlowName='Sign Up'
          analyticsMethodName='SMS'
        >
          {phoneNumber ? (
            <MfaCodePageText>
              Enter the 6 digit code we sent to your mobile number {phoneNumber}
            </MfaCodePageText>
          ) : null}
        </VerificationCodeForm>
        {verificationAttempts > 2 && (
          <Button color='tertiary_grey' isFullWidth size='md' onClick={() => onCompleted(true)}>
            I'll do this later
          </Button>
        )}
      </Flex>

      {isQueryLoading ? <Spinner /> : null}
    </>
  )
}
